import { Injector } from '@angular/core';
import { TokenType } from '@shared/shared/auth/common/constants/token-type';
import { BaseAuthTokensStorageService } from '@shared/shared/auth/data-access/base-auth-tokens-storage.service';
import { User } from '@shared/api/api-loop/models/user';
import { UserInLoop } from '@shared/modules/user-manager/data-access/users-in-loop.service';
import { combineLatest, map, Observable, of, take } from 'rxjs';
import { StorageKey, StorageService } from '@dta/shared/services/storage/storage.service';
import { LoginUserModel } from '@dta/shared/models-api-loop/contact/contact.model';
import { AuthUser } from '@dta/shared/models/auth.model';

export function migration_v1$(injector: Injector): Observable<void> {
  const storageService = injector.get(StorageService);
  const baseAuthTokensStorageService = injector.get(BaseAuthTokensStorageService);

  const users: UserInLoop[] = storageService.getParsedItem(StorageKey.users_v1);
  if (users?.length) {
    return of(undefined);
  }

  const legacyUsers: LoginUserModel[] = storageService.getParsedItem(StorageKey.users);
  if (!legacyUsers?.length) {
    return of(undefined);
  }

  storageService.setStringifiedItem(
    StorageKey.users_v1,
    legacyUsers.map(user => {
      return {
        id: user.id,
        name: user.name,
        displayName: user.displayName,
        email: user.email
      } satisfies Pick<User, 'id' | 'name' | 'displayName' | 'email'>;
    })
  );

  const observables$ = [];
  legacyUsers.forEach(user => {
    if (!user.id || !user.email) {
      return;
    }
    const emailKey = storageService.getKey(user.email, StorageKey.userAuth);
    const auth: AuthUser | undefined =
      storageService.getParsedItem(emailKey, true) ?? storageService.getParsedItem(emailKey);
    if (auth?.token) {
      if (auth.token.accessToken) {
        observables$.push(baseAuthTokensStorageService.storeToken(user.id, TokenType.access, auth.token.accessToken));
      }

      if (auth.token.refreshToken) {
        observables$.push(baseAuthTokensStorageService.storeToken(user.id, TokenType.refresh, auth.token.refreshToken));
      }
    }
    storageService.removeItem(emailKey);
  });

  const lastEmail: string = storageService.getItem(StorageKey.lastUser);
  if (lastEmail) {
    const lastUser = legacyUsers.find(u => u.email === lastEmail);
    if (lastUser?.id) {
      storageService.setItem(StorageKey.activeUserId, lastUser.id);
    }
  } else {
    storageService.setItem(StorageKey.activeUserId, legacyUsers[0]?.id);
  }

  storageService.removeItem(StorageKey.users);
  storageService.removeItem(StorageKey.lastUser);
  if (!observables$.length) {
    return of(undefined);
  }

  return combineLatest(observables$).pipe(
    take(1),
    map(() => undefined)
  );
}
